import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { SidebarState, MenuItem } from '../types/sidebar';

const initialState: SidebarState = {
  isOpen: true,
  activeRoute: '',
  expandedMenus: {},
  menuItems: {
    misMenuList: [],
    accountAggregatorList: [],
    fipList: [],
    purposeList: [],
    consentTemplateList: [],
    consentRequestList: [],
    analyticsList: [],
    pdfAnalyticsList: [],
    centralRegistryList: [],
    finotificationsList: [],
    dataFetchRequestList: [],
    settingsList: [],
    manageAppsList: [],
    adminList: [],
    integrationDocs: [],
    bulkConsentRequestList: [],
    bulkDataFetchList: [],
    bulkConsentRevokeList: [],
    analyticsPortalList: [],
  },
  userInfo: {
    isSuperAdmin: false,
    isApplicationOwner: false,
    isSupportingOfficer: false,
    userRole: '',
  },
  funnelView: false,
};

const sidebarSlice = createSlice({
  name: 'sidebar',
  initialState,
  reducers: {
    toggleSidebar: (state) => {
      state.isOpen = !state.isOpen;
    },
    setActiveRoute: (state, action: PayloadAction<string>) => {
      // Strip query parameters so sidebar matching works with routes like /dashboard/insights/ews?batch_id=...
      const route = action.payload.split('?')[0];
      state.activeRoute = route;
    },
    toggleMenu: (state, action: PayloadAction<string>) => {
      state.expandedMenus[action.payload] = !state.expandedMenus[action.payload];
      console.log('SidebarSlice: expandedMenus after toggle:', state.expandedMenus); // Added log
    },
    setMenuItems: (state, action: PayloadAction<Partial<SidebarState['menuItems']>>) => {
      state.menuItems = { ...state.menuItems, ...action.payload };
    },
    setUserInfo: (state, action: PayloadAction<SidebarState['userInfo']>) => {
      state.userInfo = action.payload;
    },
    setFunnelView: (state, action: PayloadAction<boolean>) => {
      state.funnelView = action.payload;
    },
    initializeSidebar: (state, action: PayloadAction<{ 
      menuList: MenuItem[]; 
      userInfo: SidebarState['userInfo'];
      funnelView: boolean;
    }>) => {
      const { menuList, userInfo, funnelView } = action.payload;
      
      // Filter menu items by code similar to Angular implementation
      state.menuItems.misMenuList = menuList.filter(item => item.code === 'MIS');
      state.menuItems.accountAggregatorList = menuList.filter(item => item.code === 'ACCOUNT-AGGREGATOR');
      state.menuItems.fipList = menuList.filter(item => item.code === 'FIP');
      state.menuItems.purposeList = menuList.filter(item => item.code === 'PURPOSE');
      state.menuItems.consentTemplateList = menuList.filter(item => item.code === 'CONSENT-TEMPLATE');
      state.menuItems.consentRequestList = menuList.filter(item => item.code === 'CONSENT-REQUEST');
      state.menuItems.analyticsList = menuList.filter(item => item.code === 'ANALYTICS');
      state.menuItems.pdfAnalyticsList = menuList.filter(item => item.code === 'PDF-ANALYTICS');
      state.menuItems.centralRegistryList = menuList.filter(item => item.code === 'CENTRAL-REGISTRY');
      state.menuItems.finotificationsList = menuList.filter(item => item.code === 'FI-Notifications');
      state.menuItems.dataFetchRequestList = menuList.filter(item => item.code === 'DATA-FETCH-REQUEST');
      state.menuItems.settingsList = menuList.filter(item => item.code === 'SETTINGS');
      state.menuItems.manageAppsList = menuList.filter(item => item.code === 'MANAGE-APPS');
      state.menuItems.adminList = menuList.filter(item => item.code === 'ADMIN');
      state.menuItems.integrationDocs = menuList.filter(item => item.code === 'INTEGRATIONDOCS');
      state.menuItems.bulkConsentRequestList = menuList.filter(item => item.code === 'BULK-CONSENT-REQUEST');
      state.menuItems.bulkDataFetchList = menuList.filter(item => item.code === 'BULK-DATA-FETCH');
      state.menuItems.bulkConsentRevokeList = menuList.filter(item => item.code === 'BULK-CONSENT-REVOKE');
      state.menuItems.analyticsPortalList = menuList.filter(item => item.code === 'ANALYTICS-PORTAL');
      
      state.userInfo = userInfo;
      state.funnelView = funnelView;
    },
  },
});

export const {
  toggleSidebar,
  setActiveRoute,
  toggleMenu,
  setMenuItems,
  setUserInfo,
  setFunnelView,
  initializeSidebar,
} = sidebarSlice.actions;

export default sidebarSlice.reducer; 